/**
 * City Information Component
 * 
 * Displays layout metadata (Layout Name, Links, Creator, WaLa, MapType, Remarks, Notes)
 */

export class CityInformation {
    constructor(containerElement, callbacks = {}) {
        this.container = containerElement;
        this.layoutMetadata = null;
        this.callbacks = callbacks; // { getShareString, getLongLink, getTroopNumbers, getLayoutResources, setWaterMode } - callbacks to get current layout and update terrain
    }
    
    /**
     * Set layout metadata and render
     */
    setLayoutMetadata(metadata) {
        this.layoutMetadata = metadata;
        this.render();
    }
    
    /**
     * Clear layout metadata
     */
    clearLayoutMetadata() {
        this.layoutMetadata = null;
        this.render();
    }
    
    /**
     * Render the city information section
     */
    async render() {
        const layout = this.layoutMetadata;
        const baseUrl = this.getBaseUrl();
        
        // If no layout metadata, show submit form for new layout
        if (!layout) {
            this.renderSubmitForm();
            return;
        }
        
        // If layout has ID, show display view (read-only)
        if (layout.id) {
            await this.renderDisplayView(layout, baseUrl);
        } else {
            // If no ID, show submit form (new layout)
            this.renderSubmitForm(layout);
        }
    }
    
    /**
     * Render submit form for new layouts
     */
    renderSubmitForm(existingData = null) {
        let html = `
            <div class="city-information">
                <h3>City Information</h3>
                
                <div class="submit-layout-section">
                    <div class="submit-layout-form">
                        <div class="checkbox-options-row" style="gap: 20px;">
                            <label class="checkbox-label" style="display: flex; align-items: center; gap: 6px;">
                                <input type="checkbox" id="submit-layout-private" 
                                    ${existingData?.private ? 'checked' : ''}>
                                Private
                            </label>
                            <div style="display: flex; align-items: center; gap: 6px;">
                                <button type="button" id="submit-layout-favorite" class="favorite-star-btn ${existingData?.is_favorite ? 'active' : ''}" 
                                    title="${existingData?.is_favorite ? 'Remove from favorites' : 'Add to favorites'}"
                                    aria-label="Favorite">
                                    <span class="star-icon">${existingData?.is_favorite ? '★' : '☆'}</span>
                                </button>
                                <span style="font-size: 11px; color: var(--text-color);">Favorite</span>
                            </div>
                        </div>
                        
                        <div class="checkbox-options-row" style="margin-top: 10px; gap: 20px;">
                            <label class="checkbox-label" style="display: flex; align-items: center; gap: 6px;">
                                <input type="checkbox" id="submit-include-resources" checked>
                                Include Resources
                            </label>
                            <label class="checkbox-label" style="display: flex; align-items: center; gap: 6px;">
                                <input type="checkbox" id="submit-include-troops" checked>
                                Include Troops
                            </label>
                        </div>
                        
                        <div>
                            <label>Layout Name *</label>
                            <input type="text" id="submit-layout-name" 
                                value="${existingData?.LayoutName ? this.escapeHtml(existingData.LayoutName) : ''}"
                                placeholder="Auto-generated name">
                        </div>
                        
                        <div>
                            <label>Creator</label>
                            <input type="text" id="submit-layout-creator" 
                                value="${existingData?.Creator ? this.escapeHtml(existingData.Creator) : ''}"
                                placeholder="Auto-generated">
                        </div>
                        
                        <div>
                            <label>Short Link</label>
                            <input type="text" id="submit-layout-shortlink" 
                                value="${existingData?.ShortLink ? this.escapeHtml(existingData.ShortLink) : ''}"
                                placeholder="aB3cD9xYz" 
                                maxlength="9">
                            <small>Auto-generated if blank</small>
                        </div>
                        
                        <div>
                            <label>Map Type</label>
                            <input type="text" id="submit-layout-maptype" 
                                value="${existingData?.mapType ? this.escapeHtml(existingData.mapType) : ''}"
                                placeholder="Auto-generated">
                        </div>
                        
                        <div>
                            <label>WaLa</label>
                            <select id="submit-layout-wala">
                                <option value="">Auto-detect</option>
                                <option value="Water" ${existingData?.WaLa === 'Water' ? 'selected' : ''}>Water</option>
                                <option value="Land" ${existingData?.WaLa === 'Land' ? 'selected' : ''}>Land</option>
                            </select>
                        </div>
                        
                        <div>
                            <label>Remarks</label>
                            <textarea id="submit-layout-remarks" 
                                placeholder="Optional remarks" 
                                rows="2">${existingData?.Remarks ? this.escapeHtml(existingData.Remarks) : ''}</textarea>
                        </div>
                        
                        <div>
                            <label>Notes</label>
                            <textarea id="submit-layout-notes" 
                                placeholder="Optional notes" 
                                rows="2">${existingData?.Notes ? this.escapeHtml(existingData.Notes) : ''}</textarea>
                        </div>
                    </div>
                </div>
                
                <div id="submit-layout-message"></div>
                
                <button type="button" id="btn-submit-layout" class="btn-primary">
                    Save Layout
                </button>
            </div>
        `;
        
        this.container.innerHTML = html;
        this.setupSubmitForm();
    }
    
    /**
     * Render display view for existing layouts (read-only)
     */
    async renderDisplayView(layout, baseUrl) {
        // Check if user is logged in and if layout has resource levels or troop numbers
        let isLoggedIn = false;
        let hasResourceLevels = false;
        let hasTroopNumbers = false;
        let layoutResources = null;
        let troopNumbers = null;
        
        try {
            // Check auth status
            const authApiBase = this.getAuthApiBase();
            const authResponse = await fetch(`${authApiBase}check.php`);
            if (authResponse.ok) {
                const contentType = authResponse.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                    const authData = await authResponse.json();
                    isLoggedIn = authData.loggedIn === true;
                }
            }
        } catch (error) {
            // Silently fail - auth check is optional (old PHP endpoint may not exist)
            // This is fine for planner mode
        }
        
        // Parse LayoutResources if available
        if (layout.LayoutResources) {
            try {
                layoutResources = typeof layout.LayoutResources === 'string' 
                    ? JSON.parse(layout.LayoutResources) 
                    : layout.LayoutResources;
                if (Array.isArray(layoutResources) && layoutResources.length >= 23) {
                    hasResourceLevels = true;
                }
            } catch (error) {
                console.log('Failed to parse LayoutResources:', error);
            }
        }
        
        // Parse TroopNumbers if available
        if (layout.TroopNumbers) {
            try {
                troopNumbers = typeof layout.TroopNumbers === 'string' 
                    ? JSON.parse(layout.TroopNumbers) 
                    : layout.TroopNumbers;
                if (Array.isArray(troopNumbers) && troopNumbers.length >= 17) {
                    // Check if there are any non-zero values
                    hasTroopNumbers = troopNumbers.some(count => count > 0);
                }
            } catch (error) {
                console.log('Failed to parse TroopNumbers:', error);
            }
        }
        
        let html = `
            <div class="city-information">
                <h3>City Information</h3>
                
                ${layout.LayoutName ? `
                    <div class="info-item">
                        <label>Layout Name:</label>
                        <div class="info-value">${this.escapeHtml(layout.LayoutName)}</div>
                    </div>
                ` : ''}
                ${layout.id ? `
                    <div class="info-item">
                        <label>Short Code Link:</label>
                        <div class="info-value copyable-link" data-link="${this.getRootUrl()}/${this.encodeShortCode(layout.id)}" title="Click to copy">
                            <span class="link-text">${this.getRootUrl()}/${this.encodeShortCode(layout.id)}</span>
                            <button class="copy-btn" title="Copy to clipboard">📋</button>
                        </div>
                    </div>
                ` : ''}
                ${layout.ShortLink ? `
                    <div class="info-item">
                        <label>Short Link:</label>
                        <div class="info-value copyable-link" data-link="${baseUrl.replace(/\/$/, '')}/api/maps/short/${layout.ShortLink}${layout.ShortCode ? `?shortCode=${layout.ShortCode}` : ''}" title="Click to copy">
                            <span class="link-text">${baseUrl.replace(/\/$/, '')}/api/maps/short/${layout.ShortLink}${layout.ShortCode ? `?shortCode=${layout.ShortCode}` : ''}</span>
                            <button class="copy-btn" title="Copy to clipboard">📋</button>
                        </div>
                    </div>
                ` : ''}
                ${layout.LongLink ? `
                    <div class="info-item">
                        <label>Long Link:</label>
                        <div class="info-value copyable-link" data-link="${baseUrl}?map=${encodeURIComponent(layout.LongLink)}${layout.id ? `&layoutId=${layout.id}` : layout.ShortCode ? `&shortCode=${layout.ShortCode}` : ''}" title="Click to copy">
                            <span class="link-text">${baseUrl}?map=${this.truncateText(layout.LongLink, 40)}...${layout.id ? `&layoutId=${layout.id}` : layout.ShortCode ? `&shortCode=${layout.ShortCode}` : ''}</span>
                            <button class="copy-btn" title="Copy to clipboard">📋</button>
                        </div>
                    </div>
                ` : ''}
                ${layout.Creator ? `
                    <div class="info-item">
                        <label>Created By:</label>
                        <div class="info-value">${this.escapeHtml(layout.Creator)}</div>
                    </div>
                ` : ''}
                ${layout.WaLa ? `
                    <div class="info-item">
                        <label>Water or Land:</label>
                        <div class="info-value">${this.escapeHtml(layout.WaLa)}</div>
                    </div>
                ` : ''}
                ${layout.mapType ? `
                    <div class="info-item">
                        <label>Map Type:</label>
                        <div class="info-value">${this.escapeHtml(layout.mapType)}</div>
                    </div>
                ` : ''}
                ${layout.Remarks ? `
                    <div class="info-item">
                        <label>Remarks:</label>
                        <div class="info-value remarks-content">${this.escapeHtml(layout.Remarks)}</div>
                    </div>
                ` : ''}
                ${layout.Notes ? `
                    <div class="info-item">
                        <label>Notes:</label>
                        <div class="info-value notes-content">${this.escapeHtml(layout.Notes)}</div>
                    </div>
                ` : ''}
                
                ${isLoggedIn && hasTroopNumbers ? `
                    <div class="troop-numbers-edit-section" style="margin-top: 1.5rem; padding-top: 1.5rem; border-top: 1px solid var(--border-color, #444);">
                        <h4 style="margin-bottom: 1rem;">Edit Troop Numbers</h4>
                        <p style="font-size: 0.875rem; color: var(--text-secondary, #ccc); margin-bottom: 1rem;">
                            Modify the troop numbers below and save as a new layout. The original layout will remain unchanged.
                        </p>
                        
                        <div class="troop-numbers-form">
                            <div class="troop-numbers-grid">
                                <div class="troop-number-item">
                                    <label>Guard:</label>
                                    <input type="number" id="edit-troop-0" value="${troopNumbers[0] || 0}" min="0" step="1">
                                </div>
                                <div class="troop-number-item">
                                    <label>Ballista:</label>
                                    <input type="number" id="edit-troop-1" value="${troopNumbers[1] || 0}" min="0" step="1">
                                </div>
                                <div class="troop-number-item">
                                    <label>Ranger:</label>
                                    <input type="number" id="edit-troop-2" value="${troopNumbers[2] || 0}" min="0" step="1">
                                </div>
                                <div class="troop-number-item">
                                    <label>Triari:</label>
                                    <input type="number" id="edit-troop-3" value="${troopNumbers[3] || 0}" min="0" step="1">
                                </div>
                                <div class="troop-number-item">
                                    <label>Priestess:</label>
                                    <input type="number" id="edit-troop-4" value="${troopNumbers[4] || 0}" min="0" step="1">
                                </div>
                                <div class="troop-number-item">
                                    <label>Vanquisher:</label>
                                    <input type="number" id="edit-troop-5" value="${troopNumbers[5] || 0}" min="0" step="1">
                                </div>
                                <div class="troop-number-item">
                                    <label>Sorceres:</label>
                                    <input type="number" id="edit-troop-6" value="${troopNumbers[6] || 0}" min="0" step="1">
                                </div>
                                <div class="troop-number-item">
                                    <label>Scout:</label>
                                    <input type="number" id="edit-troop-7" value="${troopNumbers[7] || 0}" min="0" step="1">
                                </div>
                                <div class="troop-number-item">
                                    <label>Arbalist:</label>
                                    <input type="number" id="edit-troop-8" value="${troopNumbers[8] || 0}" min="0" step="1">
                                </div>
                                <div class="troop-number-item">
                                    <label>Praetor:</label>
                                    <input type="number" id="edit-troop-9" value="${troopNumbers[9] || 0}" min="0" step="1">
                                </div>
                                <div class="troop-number-item">
                                    <label>Horseman:</label>
                                    <input type="number" id="edit-troop-10" value="${troopNumbers[10] || 0}" min="0" step="1">
                                </div>
                                <div class="troop-number-item">
                                    <label>Druid:</label>
                                    <input type="number" id="edit-troop-11" value="${troopNumbers[11] || 0}" min="0" step="1">
                                </div>
                                <div class="troop-number-item">
                                    <label>Battering Ram:</label>
                                    <input type="number" id="edit-troop-12" value="${troopNumbers[12] || 0}" min="0" step="1">
                                </div>
                                <div class="troop-number-item">
                                    <label>Scorpion:</label>
                                    <input type="number" id="edit-troop-13" value="${troopNumbers[13] || 0}" min="0" step="1">
                                </div>
                                <div class="troop-number-item">
                                    <label>Galley:</label>
                                    <input type="number" id="edit-troop-14" value="${troopNumbers[14] || 0}" min="0" step="1">
                                </div>
                                <div class="troop-number-item">
                                    <label>Stinger:</label>
                                    <input type="number" id="edit-troop-15" value="${troopNumbers[15] || 0}" min="0" step="1">
                                </div>
                                <div class="troop-number-item">
                                    <label>Warship:</label>
                                    <input type="number" id="edit-troop-16" value="${troopNumbers[16] || 0}" min="0" step="1">
                                </div>
                                <div class="troop-number-item">
                                    <label>Senator:</label>
                                    <input type="number" id="edit-troop-17" value="${troopNumbers[17] || 0}" min="0" step="1">
                                </div>
                            </div>
                            
                            <div id="save-as-troop-message" style="margin-top: 1rem; display: none;"></div>
                            
                            <button type="button" id="btn-save-as-new-troop" class="btn-primary" style="margin-top: 1rem;">
                                Save As New Layout
                            </button>
                        </div>
                    </div>
                ` : ''}
                
                ${isLoggedIn && layout.id ? `
                    <div class="save-as-new-section" style="margin-top: 1.5rem; padding-top: 1.5rem; border-top: 1px solid var(--border-color, #444);">
                        <p style="font-size: 0.875rem; color: var(--text-secondary, #ccc); margin-bottom: 1rem;">
                            Save the current layout (including any changes to resources or troops) as a new layout. The original layout will remain unchanged.
                        </p>
                        <div id="save-as-new-message" style="margin-bottom: 1rem; display: none;"></div>
                        <button type="button" id="btn-save-as-new-layout" class="btn-primary">
                            Save As New Layout
                        </button>
                    </div>
                ` : ''}
            </div>
        `;
        
        this.container.innerHTML = html;
        this.setupCopyButtons();
        
        // Setup save as new layout functionality if resource editing is available and layout has ID
        if (isLoggedIn && layout.id) {
            this.setupSaveAsNewLayout(layout);
        }
        
        // Setup save as new layout functionality if troop editing is available
        if (isLoggedIn && hasTroopNumbers) {
            this.setupTroopNumberEditing(layout, troopNumbers);
        }
    }
    
    /**
     * Setup submit form for new layouts
     */
    async setupSubmitForm() {
        const submitBtn = document.getElementById('btn-submit-layout');
        if (!submitBtn) return;
        
        // Resource section is now always visible for new layouts
        // No need to check login status - section is visible by default
        
        // Auto-generate suggestions on load
        this.generateSuggestions();
        
        // Listen for WaLa changes and update the game engine
        const walaSelect = document.getElementById('submit-layout-wala');
        if (walaSelect) {
            walaSelect.addEventListener('change', (e) => {
                const walaValue = e.target.value;
                if (walaValue === 'Water' || walaValue === 'Land') {
                    const isWater = walaValue === 'Water';
                    // Update game engine terrain mode if callback is available
                    if (this.callbacks.setWaterMode) {
                        this.callbacks.setWaterMode(isWater);
                    }
                }
            });
        }
        
        submitBtn.addEventListener('click', async () => {
            await this.handleSubmitLayout();
        });
        
        // Setup favorite star button toggle
        const favoriteBtn = document.getElementById('submit-layout-favorite');
        if (favoriteBtn) {
            favoriteBtn.addEventListener('click', () => {
                const isActive = favoriteBtn.classList.contains('active');
                const starIcon = favoriteBtn.querySelector('.star-icon');
                
                if (isActive) {
                    favoriteBtn.classList.remove('active');
                    if (starIcon) starIcon.textContent = '☆';
                    favoriteBtn.title = 'Add to favorites';
                } else {
                    favoriteBtn.classList.add('active');
                    if (starIcon) starIcon.textContent = '★';
                    favoriteBtn.title = 'Remove from favorites';
                }
            });
        }
    }
    
    /**
     * Generate auto-suggestions for layout fields
     */
    async generateSuggestions() {
        // Get current layout string
        let layoutString = null;
        if (this.callbacks.getLongLink) {
            layoutString = this.callbacks.getLongLink();
        } else if (this.callbacks.getShareString) {
            layoutString = this.callbacks.getShareString();
        }
        
        if (!layoutString) return;
        
        try {
            // Determine API base path
            let apiBase = '/api/layouts/';
            let authApiBase = '/api/auth/';
            const scriptPath = window.location.pathname;
            const hostname = window.location.hostname;
            
            // Check for root domains
            if (hostname.includes('cotg.rlmglobal.com') || 
                (hostname.includes('cotgopt.com') && !scriptPath.includes('/cotgopt/'))) {
                apiBase = '/api/layouts/';
                authApiBase = '/api/auth/';
            } else if (scriptPath.includes('/Louopt/')) {
                apiBase = '/Louopt/api/layouts/';
                authApiBase = '/Louopt/api/auth/';
            } else if (scriptPath.includes('/cotgopt/')) {
                apiBase = '/cotgopt/api/layouts/';
                authApiBase = '/cotgopt/api/auth/';
            } else if (scriptPath.includes('/cotg/')) {
                apiBase = '/cotg/api/layouts/';
                authApiBase = '/cotg/api/auth/';
            }
            
            // Check if user is logged in and auto-fill Creator field
            try {
                const authResponse = await fetch(`${authApiBase}check.php`);
                if (authResponse.ok) {
                    const contentType = authResponse.headers.get('content-type');
                    if (contentType && contentType.includes('application/json')) {
                        const authData = await authResponse.json();
                        if (authData.loggedIn && authData.user && authData.user.username) {
                            const creatorInput = document.getElementById('submit-layout-creator');
                            if (creatorInput && !creatorInput.value) {
                                creatorInput.value = authData.user.username;
                            }
                        }
                    }
                }
            } catch (authError) {
                // Silently fail - auth check is optional (old PHP endpoint may not exist)
                // This is fine for planner mode
            }
            
            // Auto-detect WaLa from layout string
            const walaSelect = document.getElementById('submit-layout-wala');
            if (walaSelect && !walaSelect.value && layoutString) {
                const firstChar = layoutString[0];
                if (firstChar === 'W' || firstChar === 'w') {
                    walaSelect.value = 'Water';
                } else if (firstChar === 'L' || firstChar === 'l') {
                    walaSelect.value = 'Land';
                }
            }
            
            // Try to get suggestions from autofill if we have a layout ID (for updates)
            if (this.layoutMetadata && this.layoutMetadata.id) {
                const autofillResponse = await fetch(`${apiBase}autofill.php`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ id: this.layoutMetadata.id })
                });
                
                if (autofillResponse.ok) {
                    const autofillData = await autofillResponse.json();
                    if (autofillData.success && autofillData.layout) {
                        const suggestions = autofillData.layout;
                        
                        // Fill in empty fields with suggestions (as placeholders/hints)
                        const nameInput = document.getElementById('submit-layout-name');
                        const creatorInput = document.getElementById('submit-layout-creator');
                        const mapTypeInput = document.getElementById('submit-layout-maptype');
                        
                        if (nameInput && !nameInput.value && suggestions.LayoutName) {
                            nameInput.placeholder = suggestions.LayoutName + ' (suggested)';
                            nameInput.setAttribute('data-suggestion', suggestions.LayoutName);
                        }
                        
                        if (creatorInput && !creatorInput.value && suggestions.Creator) {
                            creatorInput.placeholder = suggestions.Creator + ' (suggested)';
                            creatorInput.setAttribute('data-suggestion', suggestions.Creator);
                        }
                        
                        if (mapTypeInput && !mapTypeInput.value && suggestions.mapType) {
                            mapTypeInput.placeholder = suggestions.mapType + ' (suggested)';
                            mapTypeInput.setAttribute('data-suggestion', suggestions.mapType);
                        }
                    }
                }
            }
        } catch (error) {
            console.log('Failed to generate suggestions:', error);
        }
    }
    
    /**
     * Handle submitting new layout
     */
    async handleSubmitLayout() {
        const submitBtn = document.getElementById('btn-submit-layout');
        const messageEl = document.getElementById('submit-layout-message');
        
        if (!submitBtn) return;
        
        // Get current layout strings
        let sharestring = '';
        let longLink = '';
        
        if (this.callbacks.getShareString) {
            sharestring = this.callbacks.getShareString();
        }
        if (this.callbacks.getLongLink) {
            longLink = this.callbacks.getLongLink();
        }
        
        if (!sharestring && !longLink) {
            this.showSubmitMessage('error', 'No layout data available. Please load or create a layout first.');
            return;
        }
        
        // Get form values
        const layoutName = document.getElementById('submit-layout-name')?.value.trim() || '';
        const creator = document.getElementById('submit-layout-creator')?.value.trim() || '';
        const shortLink = document.getElementById('submit-layout-shortlink')?.value.trim() || '';
        const mapType = document.getElementById('submit-layout-maptype')?.value.trim() || '';
        const wala = document.getElementById('submit-layout-wala')?.value || '';
        const remarks = document.getElementById('submit-layout-remarks')?.value.trim() || '';
        const notes = document.getElementById('submit-layout-notes')?.value.trim() || '';
        const isPrivate = document.getElementById('submit-layout-private')?.checked || false;
        
        // Use suggestions if fields are empty
        const finalName = layoutName || document.getElementById('submit-layout-name')?.getAttribute('data-suggestion') || '';
        const finalCreator = creator || document.getElementById('submit-layout-creator')?.getAttribute('data-suggestion') || '';
        const finalMapType = mapType || document.getElementById('submit-layout-maptype')?.getAttribute('data-suggestion') || '';
        
        if (!finalName) {
            this.showSubmitMessage('error', 'Layout Name is required');
            return;
        }
        
        // Disable button and show loading
        submitBtn.disabled = true;
        const originalText = submitBtn.textContent;
        submitBtn.textContent = 'Submitting...';
        if (messageEl) {
            messageEl.style.display = 'none';
        }
        
        try {
            // Determine API base path
            let apiBase = '/api/layouts/';
            const scriptPath = window.location.pathname;
            if (scriptPath.includes('/Louopt/')) {
                apiBase = '/Louopt/api/layouts/';
            } else if (scriptPath.includes('/cotgopt/')) {
                apiBase = '/cotgopt/api/layouts/';
            }
            
            // Check if user wants to include resources and troops
            const includeResources = document.getElementById('submit-include-resources')?.checked ?? true;
            const includeTroops = document.getElementById('submit-include-troops')?.checked ?? true;
            
            // Get troop numbers from MilitaryPanel callback (only if checkbox is checked)
            let troopNumbers = null;
            if (includeTroops && this.callbacks.getTroopNumbers) {
                const troopArray = this.callbacks.getTroopNumbers();
                // Always include troop numbers if available (even if all zeros)
                if (troopArray && Array.isArray(troopArray) && troopArray.length === 18) {
                    troopNumbers = troopArray;
                }
            }
            
            // Get layout resources from ResourcePanel callback (only if checkbox is checked)
            let layoutResources = null;
            if (includeResources && this.callbacks.getLayoutResources) {
                const resourceArray = this.callbacks.getLayoutResources();
                // Always include if valid array (needs at least 23 elements for the full format)
                if (resourceArray && Array.isArray(resourceArray) && resourceArray.length >= 23) {
                    layoutResources = resourceArray;
                }
            }
            
            // Build request body
            const requestBody = {
                LayoutName: finalName,
                Sharestring: sharestring || '',
                Creator: finalCreator || '',
                ShortLink: shortLink || '',
                LongLink: longLink || '',
                mapType: finalMapType || '',
                WaLa: wala || '',
                Remarks: remarks || '',
                Notes: notes || '',
                private: isPrivate ? 1 : 0
            };
            
            // Add troop numbers if available
            if (troopNumbers) {
                requestBody.TroopNumbers = troopNumbers;
            }
            
            // Add layout resources if available (always include if form exists, even if all zeros)
            if (layoutResources) {
                requestBody.LayoutResources = layoutResources;
            }
            
            // Submit layout
            const response = await fetch(`${apiBase}submit.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            });
            
            const data = await response.json();
            
            if (response.ok && data.success) {
                // Update local metadata with the new layout data
                if (data.layout) {
                    this.setLayoutMetadata({
                        id: data.layout.id || '',
                        LayoutName: data.layout.LayoutName || '',
                        ShortLink: data.layout.ShortLink || '',
                        LongLink: data.layout.LongLink || '',
                        Sharestring: data.layout.Sharestring || '',
                        Creator: data.layout.Creator || '',
                        WaLa: data.layout.WaLa || '',
                        mapType: data.layout.mapType || '',
                        Remarks: data.layout.Remarks || '',
                        Notes: data.layout.Notes || '',
                        private: data.layout.private || 0
                    });
                }
                
                // Handle favorite if star is active
                const favoriteBtn = document.getElementById('submit-layout-favorite');
                if (favoriteBtn && favoriteBtn.classList.contains('active') && data.layout?.id) {
                    // Check if user is logged in before attempting to favorite
                    try {
                        const authCheckResponse = await fetch(`${apiBase.replace('/layouts/', '/auth/')}check.php`);
                        if (authCheckResponse.ok) {
                            const contentType = authCheckResponse.headers.get('content-type');
                            if (contentType && contentType.includes('application/json')) {
                                const authData = await authCheckResponse.json();
                                if (authData.loggedIn) {
                                    // User is logged in, proceed with favorite
                                    try {
                                        const favoriteResponse = await fetch(`${apiBase}favorites.php`, {
                                            method: 'POST',
                                            headers: {
                                                'Content-Type': 'application/json'
                                            },
                                            body: JSON.stringify({ layout_id: data.layout.id })
                                        });
                                        // Silently handle favorite - don't show error if it fails
                                        if (favoriteResponse.ok) {
                                            const favoriteData = await favoriteResponse.json();
                                            if (favoriteData.success) {
                                                console.log('Layout added to favorites');
                                            }
                                        } else if (favoriteResponse.status === 401) {
                                            // User not authenticated - silently skip
                                            // This can happen if session expired between auth check and favorite request
                                        }
                                    } catch (error) {
                                        // Silently fail - favorite is optional
                                        console.log('Failed to add favorite:', error);
                                    }
                                }
                                // If not logged in, silently skip - favorite requires authentication
                            }
                        }
                    } catch (error) {
                        // Silently fail auth check - favorite is optional
                        console.log('Failed to check auth status for favorite:', error);
                    }
                }
                
                // Show success with green checkmark and flash
                this.showSubmitSuccess(submitBtn);
            } else {
                this.showSubmitMessage('error', data.error || 'Failed to submit layout');
                submitBtn.disabled = false;
                submitBtn.textContent = originalText;
            }
        } catch (error) {
            this.showSubmitMessage('error', 'Failed to submit layout: ' + error.message);
            submitBtn.disabled = false;
            submitBtn.textContent = originalText;
        }
    }
    
    /**
     * Show submit success with green checkmark and flash
     */
    showSubmitSuccess(submitBtn) {
        // Change button to green checkmark
        const originalText = submitBtn.textContent;
        submitBtn.textContent = '✓ Saved';
        submitBtn.style.backgroundColor = '#4caf50';
        submitBtn.style.color = 'white';
        
        // Flash effect on the form
        const form = document.getElementById('submit-layout-form') || document.querySelector('.submit-layout-form');
        if (form) {
            form.style.backgroundColor = 'rgba(76, 175, 80, 0.2)';
            form.style.transition = 'background-color 0.3s ease';
            setTimeout(() => {
                form.style.backgroundColor = '';
            }, 1000);
        }
        
        // Reset button after 2 seconds (but form will switch to display view)
        setTimeout(() => {
            submitBtn.textContent = originalText;
            submitBtn.style.backgroundColor = '';
            submitBtn.style.color = '';
            submitBtn.disabled = false;
        }, 2000);
    }
    
    /**
     * Show submit message
     */
    showSubmitMessage(type, message) {
        const messageEl = document.getElementById('submit-layout-message');
        if (!messageEl) return;
        
        messageEl.style.display = 'block';
        messageEl.textContent = message;
        messageEl.style.backgroundColor = type === 'error' ? 'rgba(244, 67, 54, 0.2)' : 'rgba(76, 175, 80, 0.2)';
        messageEl.style.color = type === 'error' ? '#f44336' : '#4caf50';
        messageEl.style.border = `1px solid ${type === 'error' ? '#f44336' : '#4caf50'}`;
        
        // Auto-hide after 5 seconds
        setTimeout(() => {
            messageEl.style.display = 'none';
        }, 5000);
    }
    
    /**
     * Setup save as new layout functionality (for resources and/or troops)
     */
    setupSaveAsNewLayout(originalLayout) {
        const saveBtn = document.getElementById('btn-save-as-new-layout');
        if (!saveBtn) return;
        
        // Store original layout data for saving
        this.originalLayoutForSave = originalLayout;
        
        saveBtn.addEventListener('click', async () => {
            await this.handleSaveAsNewLayoutWithResourcesAndTroops();
        });
    }
    
    /**
     * Setup troop number editing functionality
     */
    setupTroopNumberEditing(originalLayout, originalTroops) {
        const saveBtn = document.getElementById('btn-save-as-new-troop');
        if (!saveBtn) return;
        
        // Store original layout data for saving
        this.originalLayoutForSaveTroop = originalLayout;
        this.originalTroopsForSave = originalTroops;
        
        saveBtn.addEventListener('click', async () => {
            await this.handleSaveAsNewLayoutWithTroops();
        });
    }
    
    /**
     * Handle saving layout as new entry with edited resource levels
     * Gets resources from ResourcePanel callbacks instead of duplicate inputs
     */
    async handleSaveAsNewLayout() {
        const saveBtn = document.getElementById('btn-save-as-new');
        const messageEl = document.getElementById('save-as-message');
        
        if (!saveBtn || !this.originalLayoutForSave) return;
        
        // Get resource values from ResourcePanel callbacks
        let updatedResources = null;
        if (this.callbacks.getLayoutResources) {
            const resourceArray = this.callbacks.getLayoutResources();
            if (resourceArray && Array.isArray(resourceArray) && resourceArray.length >= 23) {
                updatedResources = resourceArray;
            }
        }
        
        // If not available from callbacks, create default array
        if (!updatedResources) {
            updatedResources = [0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0];
        }
        
        // Disable button and show loading
        saveBtn.disabled = true;
        const originalText = saveBtn.textContent;
        saveBtn.textContent = 'Saving...';
        if (messageEl) {
            messageEl.style.display = 'none';
        }
        
        try {
            // Get current layout strings
            let sharestring = '';
            let longLink = '';
            
            if (this.callbacks.getShareString) {
                sharestring = this.callbacks.getShareString();
            }
            if (this.callbacks.getLongLink) {
                longLink = this.callbacks.getLongLink();
            }
            
            if (!sharestring && !longLink) {
                this.showSaveAsMessage('error', 'No layout data available. Please load or create a layout first.');
                saveBtn.disabled = false;
                saveBtn.textContent = originalText;
                return;
            }
            
            // Determine API base path
            let apiBase = '/api/layouts/';
            const scriptPath = window.location.pathname;
            const hostname = window.location.hostname;
            
            if (hostname.includes('cotg.rlmglobal.com') ||
                (hostname.includes('cotgopt.com') && !scriptPath.includes('/cotgopt/'))) {
                apiBase = '/api/layouts/';
            } else if (scriptPath.includes('/Louopt/')) {
                apiBase = '/Louopt/api/layouts/';
            } else if (scriptPath.includes('/cotgopt/')) {
                apiBase = '/cotgopt/api/layouts/';
            } else if (scriptPath.includes('/cotg/')) {
                apiBase = '/cotg/api/layouts/';
            }
            
            // Get troop numbers if available (preserve original)
            let troopNumbers = null;
            if (this.callbacks.getTroopNumbers) {
                const troopArray = this.callbacks.getTroopNumbers();
                if (troopArray && Array.isArray(troopArray) && troopArray.some(count => count > 0)) {
                    troopNumbers = troopArray;
                }
            }
            // If not available from callbacks, try to preserve from original layout
            if (!troopNumbers && this.originalLayoutForSave.TroopNumbers) {
                try {
                    troopNumbers = typeof this.originalLayoutForSave.TroopNumbers === 'string'
                        ? JSON.parse(this.originalLayoutForSave.TroopNumbers)
                        : this.originalLayoutForSave.TroopNumbers;
                } catch (error) {
                    console.log('Failed to parse original TroopNumbers:', error);
                }
            }
            
            // Build request body (create new entry, don't include original ID)
            const requestBody = {
                LayoutName: this.originalLayoutForSave.LayoutName ? 
                    `${this.originalLayoutForSave.LayoutName} (Copy)` : 'Layout Copy',
                Sharestring: sharestring || '',
                Creator: '', // Will be auto-filled by server with logged-in username
                ShortLink: '', // Will be auto-generated
                LongLink: longLink || '',
                mapType: this.originalLayoutForSave.mapType || '',
                WaLa: this.originalLayoutForSave.WaLa || '',
                Remarks: this.originalLayoutForSave.Remarks || '',
                Notes: this.originalLayoutForSave.Notes || '',
                private: 0, // Default to public
                LayoutResources: updatedResources
            };
            
            // Add troop numbers if available
            if (troopNumbers) {
                requestBody.TroopNumbers = troopNumbers;
            }
            
            // Submit as new layout
            const response = await fetch(`${apiBase}submit.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            });
            
            const data = await response.json();
            
            if (response.ok && data.success) {
                // Update local metadata with the new layout data
                if (data.layout) {
                    this.setLayoutMetadata({
                        id: data.layout.id || '',
                        LayoutName: data.layout.LayoutName || '',
                        ShortLink: data.layout.ShortLink || '',
                        LongLink: data.layout.LongLink || '',
                        Sharestring: data.layout.Sharestring || '',
                        Creator: data.layout.Creator || '',
                        WaLa: data.layout.WaLa || '',
                        mapType: data.layout.mapType || '',
                        Remarks: data.layout.Remarks || '',
                        Notes: data.layout.Notes || '',
                        private: data.layout.private || 0,
                        LayoutResources: data.layout.LayoutResources || null
                    });
                }
                
                this.showSaveAsMessage('success', `Layout saved as new entry! (ID: ${data.layout?.id || 'N/A'})`);
                saveBtn.textContent = '✓ Saved';
                saveBtn.style.backgroundColor = '#4caf50';
                saveBtn.style.color = 'white';
                
                // Reset button after 2 seconds
                setTimeout(() => {
                    saveBtn.textContent = originalText;
                    saveBtn.style.backgroundColor = '';
                    saveBtn.style.color = '';
                    saveBtn.disabled = false;
                }, 2000);
            } else {
                this.showSaveAsMessage('error', data.error || 'Failed to save layout');
                saveBtn.disabled = false;
                saveBtn.textContent = originalText;
            }
        } catch (error) {
            this.showSaveAsMessage('error', 'Failed to save layout: ' + error.message);
            saveBtn.disabled = false;
            saveBtn.textContent = originalText;
        }
    }
    
    /**
     * Handle saving layout as new entry with resources and troops from callbacks
     */
    async handleSaveAsNewLayoutWithResourcesAndTroops() {
        const saveBtn = document.getElementById('btn-save-as-new-layout');
        const messageEl = document.getElementById('save-as-new-message');
        
        if (!saveBtn || !this.originalLayoutForSave) return;
        
        // Get resource values from ResourcePanel callbacks
        let updatedResources = null;
        if (this.callbacks.getLayoutResources) {
            const resourceArray = this.callbacks.getLayoutResources();
            if (resourceArray && Array.isArray(resourceArray) && resourceArray.length >= 23) {
                updatedResources = resourceArray;
            }
        }
        
        // Get troop numbers from MilitaryPanel callbacks
        let troopNumbers = null;
        if (this.callbacks.getTroopNumbers) {
            const troopArray = this.callbacks.getTroopNumbers();
            if (troopArray && Array.isArray(troopArray) && troopArray.length >= 18) {
                troopNumbers = troopArray;
            }
        }
        
        // Disable button and show loading
        saveBtn.disabled = true;
        const originalText = saveBtn.textContent;
        saveBtn.textContent = 'Saving...';
        if (messageEl) {
            messageEl.style.display = 'none';
        }
        
        try {
            // Get current layout strings
            let sharestring = '';
            let longLink = '';
            
            if (this.callbacks.getShareString) {
                sharestring = this.callbacks.getShareString();
            }
            if (this.callbacks.getLongLink) {
                longLink = this.callbacks.getLongLink();
            }
            
            if (!sharestring && !longLink) {
                this.showSaveAsNewMessage('error', 'No layout data available. Please load or create a layout first.');
                saveBtn.disabled = false;
                saveBtn.textContent = originalText;
                return;
            }
            
            // Determine API base path
            let apiBase = '/api/layouts/';
            const scriptPath = window.location.pathname;
            const hostname = window.location.hostname;
            
            if (hostname.includes('cotg.rlmglobal.com') ||
                (hostname.includes('cotgopt.com') && !scriptPath.includes('/cotgopt/'))) {
                apiBase = '/api/layouts/';
            } else if (scriptPath.includes('/Louopt/')) {
                apiBase = '/Louopt/api/layouts/';
            } else if (scriptPath.includes('/cotgopt/')) {
                apiBase = '/cotgopt/api/layouts/';
            } else if (scriptPath.includes('/cotg/')) {
                apiBase = '/cotg/api/layouts/';
            }
            
            // Build request body (create new entry, don't include original ID)
            const requestBody = {
                LayoutName: this.originalLayoutForSave.LayoutName ? 
                    `${this.originalLayoutForSave.LayoutName} (Copy)` : 'Layout Copy',
                Sharestring: sharestring || '',
                Creator: '', // Will be auto-filled by server with logged-in username
                ShortLink: '', // Will be auto-generated
                LongLink: longLink || '',
                mapType: this.originalLayoutForSave.mapType || '',
                WaLa: this.originalLayoutForSave.WaLa || '',
                Remarks: this.originalLayoutForSave.Remarks || '',
                Notes: this.originalLayoutForSave.Notes || '',
                private: 0 // Default to public
            };
            
            // Add resources if available
            if (updatedResources) {
                requestBody.LayoutResources = updatedResources;
            }
            
            // Add troop numbers if available
            if (troopNumbers) {
                requestBody.TroopNumbers = troopNumbers;
            }
            
            // Submit as new layout
            const response = await fetch(`${apiBase}submit.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            });
            
            const data = await response.json();
            
            if (response.ok && data.success) {
                // Update local metadata with the new layout data
                if (data.layout) {
                    this.setLayoutMetadata({
                        id: data.layout.id || '',
                        LayoutName: data.layout.LayoutName || '',
                        ShortLink: data.layout.ShortLink || '',
                        LongLink: data.layout.LongLink || '',
                        Sharestring: data.layout.Sharestring || '',
                        Creator: data.layout.Creator || '',
                        WaLa: data.layout.WaLa || '',
                        mapType: data.layout.mapType || '',
                        Remarks: data.layout.Remarks || '',
                        Notes: data.layout.Notes || '',
                        private: data.layout.private || 0
                    });
                }
                
                this.showSaveAsNewMessage('success', `Layout saved as new entry! (ID: ${data.layout?.id || 'N/A'})`);
                saveBtn.textContent = '✓ Saved';
                saveBtn.style.backgroundColor = '#4caf50';
                saveBtn.style.color = 'white';
                
                // Reset button after 2 seconds
                setTimeout(() => {
                    saveBtn.textContent = originalText;
                    saveBtn.style.backgroundColor = '';
                    saveBtn.style.color = '';
                    saveBtn.disabled = false;
                }, 2000);
            } else {
                this.showSaveAsNewMessage('error', data.error || 'Failed to save layout');
                saveBtn.disabled = false;
                saveBtn.textContent = originalText;
            }
        } catch (error) {
            this.showSaveAsNewMessage('error', 'Failed to save layout: ' + error.message);
            saveBtn.disabled = false;
            saveBtn.textContent = originalText;
        }
    }
    
    /**
     * Show save as new message
     */
    showSaveAsNewMessage(type, message) {
        const messageEl = document.getElementById('save-as-new-message');
        if (!messageEl) return;
        
        messageEl.style.display = 'block';
        messageEl.textContent = message;
        messageEl.style.backgroundColor = type === 'error' ? 'rgba(244, 67, 54, 0.2)' : 'rgba(76, 175, 80, 0.2)';
        messageEl.style.color = type === 'error' ? '#f44336' : '#4caf50';
        messageEl.style.border = `1px solid ${type === 'error' ? '#f44336' : '#4caf50'}`;
        messageEl.style.padding = '0.75rem';
        messageEl.style.borderRadius = '4px';
        
        // Auto-hide after 5 seconds
        setTimeout(() => {
            messageEl.style.display = 'none';
        }, 5000);
    }
    
    /**
     * Handle saving layout as new entry with edited troop numbers
     */
    async handleSaveAsNewLayoutWithTroops() {
        const saveBtn = document.getElementById('btn-save-as-new-troop');
        const messageEl = document.getElementById('save-as-troop-message');
        
        if (!saveBtn || !this.originalLayoutForSaveTroop || !this.originalTroopsForSave) return;
        
        // Get edited troop values (18 units: indices 0-17)
        const updatedTroops = new Array(18).fill(0);
        for (let i = 0; i < 18; i++) {
            const input = document.getElementById(`edit-troop-${i}`);
            if (input) {
                updatedTroops[i] = parseInt(input.value || '0', 10);
            } else {
                // If input doesn't exist, use original value (handles arrays shorter than 18)
                updatedTroops[i] = (this.originalTroopsForSave[i] || 0);
            }
        }
        
        // Disable button and show loading
        saveBtn.disabled = true;
        const originalText = saveBtn.textContent;
        saveBtn.textContent = 'Saving...';
        if (messageEl) {
            messageEl.style.display = 'none';
        }
        
        try {
            // Get current layout strings
            let sharestring = '';
            let longLink = '';
            
            if (this.callbacks.getShareString) {
                sharestring = this.callbacks.getShareString();
            }
            if (this.callbacks.getLongLink) {
                longLink = this.callbacks.getLongLink();
            }
            
            if (!sharestring && !longLink) {
                this.showSaveAsTroopMessage('error', 'No layout data available. Please load or create a layout first.');
                saveBtn.disabled = false;
                saveBtn.textContent = originalText;
                return;
            }
            
            // Determine API base path
            let apiBase = '/api/layouts/';
            const scriptPath = window.location.pathname;
            const hostname = window.location.hostname;
            
            if (hostname.includes('cotg.rlmglobal.com') ||
                (hostname.includes('cotgopt.com') && !scriptPath.includes('/cotgopt/'))) {
                apiBase = '/api/layouts/';
            } else if (scriptPath.includes('/Louopt/')) {
                apiBase = '/Louopt/api/layouts/';
            } else if (scriptPath.includes('/cotgopt/')) {
                apiBase = '/cotgopt/api/layouts/';
            } else if (scriptPath.includes('/cotg/')) {
                apiBase = '/cotg/api/layouts/';
            }
            
            // Get layout resources if available (preserve original)
            let layoutResources = null;
            if (this.callbacks.getLayoutResources) {
                const resourceArray = this.callbacks.getLayoutResources();
                if (resourceArray && Array.isArray(resourceArray) && resourceArray.length >= 23) {
                    layoutResources = resourceArray;
                }
            }
            // If not available from callbacks, try to preserve from original layout
            if (!layoutResources && this.originalLayoutForSaveTroop.LayoutResources) {
                try {
                    layoutResources = typeof this.originalLayoutForSaveTroop.LayoutResources === 'string'
                        ? JSON.parse(this.originalLayoutForSaveTroop.LayoutResources)
                        : this.originalLayoutForSaveTroop.LayoutResources;
                } catch (error) {
                    console.log('Failed to parse original LayoutResources:', error);
                }
            }
            
            // Build request body (create new entry, don't include original ID)
            const requestBody = {
                LayoutName: this.originalLayoutForSaveTroop.LayoutName ? 
                    `${this.originalLayoutForSaveTroop.LayoutName} (Copy)` : 'Layout Copy',
                Sharestring: sharestring || '',
                Creator: '', // Will be auto-filled by server with logged-in username
                ShortLink: '', // Will be auto-generated
                LongLink: longLink || '',
                mapType: this.originalLayoutForSaveTroop.mapType || '',
                WaLa: this.originalLayoutForSaveTroop.WaLa || '',
                Remarks: this.originalLayoutForSaveTroop.Remarks || '',
                Notes: this.originalLayoutForSaveTroop.Notes || '',
                private: 0, // Default to public
                TroopNumbers: updatedTroops
            };
            
            // Add layout resources if available
            if (layoutResources) {
                requestBody.LayoutResources = layoutResources;
            }
            
            // Submit as new layout
            const response = await fetch(`${apiBase}submit.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            });
            
            const data = await response.json();
            
            if (response.ok && data.success) {
                // Update local metadata with the new layout data
                if (data.layout) {
                    this.setLayoutMetadata({
                        id: data.layout.id || '',
                        LayoutName: data.layout.LayoutName || '',
                        ShortLink: data.layout.ShortLink || '',
                        LongLink: data.layout.LongLink || '',
                        Sharestring: data.layout.Sharestring || '',
                        Creator: data.layout.Creator || '',
                        WaLa: data.layout.WaLa || '',
                        mapType: data.layout.mapType || '',
                        Remarks: data.layout.Remarks || '',
                        Notes: data.layout.Notes || '',
                        private: data.layout.private || 0,
                        TroopNumbers: data.layout.TroopNumbers || null,
                        LayoutResources: data.layout.LayoutResources || null
                    });
                }
                
                this.showSaveAsTroopMessage('success', `Layout saved as new entry! (ID: ${data.layout?.id || 'N/A'})`);
                saveBtn.textContent = '✓ Saved';
                saveBtn.style.backgroundColor = '#4caf50';
                saveBtn.style.color = 'white';
                
                // Reset button after 2 seconds
                setTimeout(() => {
                    saveBtn.textContent = originalText;
                    saveBtn.style.backgroundColor = '';
                    saveBtn.style.color = '';
                    saveBtn.disabled = false;
                }, 2000);
            } else {
                this.showSaveAsTroopMessage('error', data.error || 'Failed to save layout');
                saveBtn.disabled = false;
                saveBtn.textContent = originalText;
            }
        } catch (error) {
            this.showSaveAsTroopMessage('error', 'Failed to save layout: ' + error.message);
            saveBtn.disabled = false;
            saveBtn.textContent = originalText;
        }
    }
    
    /**
     * Show message for save as new layout
     */
    showSaveAsMessage(type, message) {
        const messageEl = document.getElementById('save-as-message');
        if (!messageEl) return;
        
        messageEl.style.display = 'block';
        messageEl.textContent = message;
        messageEl.style.backgroundColor = type === 'error' ? 'rgba(244, 67, 54, 0.2)' : 'rgba(76, 175, 80, 0.2)';
        messageEl.style.color = type === 'error' ? '#f44336' : '#4caf50';
        messageEl.style.border = `1px solid ${type === 'error' ? '#f44336' : '#4caf50'}`;
        messageEl.style.padding = '0.5rem';
        messageEl.style.borderRadius = '4px';
        
        // Auto-hide after 5 seconds
        setTimeout(() => {
            messageEl.style.display = 'none';
        }, 5000);
    }
    
    /**
     * Show message for save as new layout (troop version)
     */
    showSaveAsTroopMessage(type, message) {
        const messageEl = document.getElementById('save-as-troop-message');
        if (!messageEl) return;
        
        messageEl.style.display = 'block';
        messageEl.textContent = message;
        messageEl.style.backgroundColor = type === 'error' ? 'rgba(244, 67, 54, 0.2)' : 'rgba(76, 175, 80, 0.2)';
        messageEl.style.color = type === 'error' ? '#f44336' : '#4caf50';
        messageEl.style.border = `1px solid ${type === 'error' ? '#f44336' : '#4caf50'}`;
        messageEl.style.padding = '0.5rem';
        messageEl.style.borderRadius = '4px';
        
        // Auto-hide after 5 seconds
        setTimeout(() => {
            messageEl.style.display = 'none';
        }, 5000);
    }
    
    /**
     * Get auth API base path
     */
    getAuthApiBase() {
        const scriptPath = window.location.pathname;
        const hostname = window.location.hostname;
        
        if (hostname.includes('cotg.rlmglobal.com') ||
            (hostname.includes('cotgopt.com') && !scriptPath.includes('/cotgopt/'))) {
            return '/api/auth/';
        } else if (scriptPath.includes('/Louopt/')) {
            return '/Louopt/api/auth/';
        } else if (scriptPath.includes('/cotgopt/')) {
            return '/cotgopt/api/auth/';
        } else if (scriptPath.includes('/cotg/')) {
            return '/cotg/api/auth/';
        }
        return '/api/auth/';
    }
    
    /**
     * Setup copy button functionality
     */
    setupCopyButtons() {
        const copyButtons = this.container.querySelectorAll('.copy-btn');
        copyButtons.forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                
                // Check if button has data-copy attribute (for ShareString text)
                const copyText = btn.getAttribute('data-copy');
                if (copyText) {
                    navigator.clipboard.writeText(copyText).then(() => {
                        const originalText = btn.textContent;
                        btn.textContent = '✓';
                        btn.style.color = '#4caf50';
                        setTimeout(() => {
                            btn.textContent = originalText;
                            btn.style.color = '';
                        }, 2000);
                    }).catch(err => {
                        console.error('Failed to copy:', err);
                        alert('Failed to copy to clipboard');
                    });
                    return;
                }
                
                // Otherwise, handle as URL link
                const linkContainer = btn.closest('.copyable-link');
                if (linkContainer) {
                    const link = linkContainer.getAttribute('data-link');
                    if (link) {
                        navigator.clipboard.writeText(link).then(() => {
                            // Show feedback
                            const originalText = btn.textContent;
                            btn.textContent = '✓';
                            btn.style.color = '#4caf50';
                            setTimeout(() => {
                                btn.textContent = originalText;
                                btn.style.color = '';
                            }, 2000);
                        }).catch(err => {
                            console.error('Failed to copy:', err);
                            alert('Failed to copy to clipboard');
                        });
                    }
                }
            });
        });
        
        // Also allow clicking the whole link container
        const copyableLinks = this.container.querySelectorAll('.copyable-link');
        copyableLinks.forEach(link => {
            link.addEventListener('click', (e) => {
                if (e.target.classList.contains('copy-btn')) return;
                const linkUrl = link.getAttribute('data-link');
                if (linkUrl) {
                    navigator.clipboard.writeText(linkUrl).then(() => {
                        link.style.backgroundColor = 'rgba(76, 175, 80, 0.2)';
                        setTimeout(() => {
                            link.style.backgroundColor = '';
                        }, 1000);
                    }).catch(err => {
                        console.error('Failed to copy:', err);
                    });
                }
            });
        });
    }
    
    /**
     * Get base URL (cotgopt.com, Louopt, or live test server based on current path)
     */
    getBaseUrl() {
        const hostname = window.location.hostname;
        const path = window.location.pathname;
        const protocol = window.location.protocol;
        const origin = `${protocol}//${hostname}`;
        
        // Check for live test server subdomain: cotg.rlmglobal.com
        if (hostname.includes('cotg.rlmglobal.com')) {
            return `${origin}/`;
        }
        
        // Check for live test server subdirectory: rlmglobal.com/cotg/
        if (hostname.includes('rlmglobal.com') && path.includes('/cotg/')) {
            return `${origin}/cotg/`;
        }
        
        // Determine if we're on cotgopt or Louopt
        if (path.includes('/cotgopt/')) {
            return hostname.includes('localhost') ? 'http://localhost/cotgopt/' : 'https://cotgopt.com/';
        } else if (path.includes('/Louopt/')) {
            return hostname.includes('localhost') ? 'http://localhost/Louopt/' : 'https://cotgopt.com/Louopt/';
        } else {
            // Default to cotgopt.com
            return hostname.includes('localhost') ? 'http://localhost/' : 'https://cotgopt.com/';
        }
    }
    
    /**
     * Get root URL (domain only, no subdirectories) for short code links
     * Returns format like: https://cotgopt.com or http://localhost
     */
    getRootUrl() {
        const hostname = window.location.hostname;
        const protocol = window.location.protocol;
        const origin = `${protocol}//${hostname}`;
        
        // For production, always use cotgopt.com
        if (!hostname.includes('localhost') && !hostname.includes('127.0.0.1')) {
            // Check if it's a known production domain
            if (hostname.includes('cotgopt.com')) {
                return 'https://cotgopt.com';
            } else if (hostname.includes('cotg.rlmglobal.com')) {
                return `${protocol}//${hostname}`;
            } else {
                // For other domains, use the current origin
                return origin;
            }
        }
        
        // For localhost, use the current origin
        return origin;
    }
    
    /**
     * Truncate text with ellipsis
     */
    truncateText(text, maxLength) {
        if (!text || text.length <= maxLength) return text;
        return text.substring(0, maxLength);
    }
    
    /**
     * Escape HTML to prevent XSS
     */
    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
    
    /**
     * Encode a layout ID into a 4-character ShortCode
     * Uses base-62 encoding (same as PHP encodeShortCode function)
     * @param {number} id - The layout ID
     * @returns {string} 4-character ShortCode
     */
    encodeShortCode(id) {
        const alphabet = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        const base = alphabet.length;
        let n = parseInt(id, 10);
        let ret = '';
        
        // Build forwards (prepend characters)
        while (n > 0) {
            ret = alphabet[n % base] + ret;
            n = Math.floor(n / base);
        }
        
        // Pad to 4 characters with leading 'a' (index 0)
        while (ret.length < 4) {
            ret = 'a' + ret;
        }
        
        // Ensure it's exactly 4 characters (take last 4 if longer)
        if (ret.length > 4) {
            ret = ret.slice(-4);
        }
        
        return ret;
    }
    
}

